import React, {useState, useEffect, useMemo} from 'react';
import {
    ExpandMore,
    ExpandLess,
    CheckCircle,
    Block
} from '@mui/icons-material';

import {
    Box,
    Button,
    Autocomplete,
    TextField,
    FormControlLabel,
    Checkbox,
    Popper,
    Collapse,
    IconButton,
    Typography,
    Tooltip
} from '@mui/material';
import {FaFingerprint} from 'react-icons/fa';

import SmileDrawerContainer from './SmilesDrawer';
import SmilesChecker from './SmilesChecker';

/**
 * Component to display the results of the prediction.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.result - The result object.
 * @param {boolean} props.excluded - Whether the domain is excluded from the submission.
 * @param {Function} props.onExcludedChange - Called with the new excluded state.
 * @returns {React.ReactElement} - The result tile component.
 */
const DomainTile = ({result, domainIndex, protein_name, onAnnotationChange, excluded = false, onExcludedChange}) => {
    const [expanded, setExpanded] = useState(false);  // Start expanded
    const toggleExpanded = () => setExpanded(prev => !prev);
    const [nameWarnings, setNameWarnings] = useState({});

    const parasResult = result["paras_result"];
    const sequence = parasResult["domain_sequence"];
    const signature = parasResult["domain_signature"];
    const extendedSignature = parasResult["domain_extended_signature"];

    const sequenceMatches = result["sequence_matches"];
    const hasSequenceMatch = sequenceMatches && sequenceMatches.length > 0;
    const firstSequenceMatch = hasSequenceMatch ? sequenceMatches[0] : null;
    const matchSynonym = hasSequenceMatch ? firstSequenceMatch["synonyms"][0]["synonym"] : null;
    const matchedSubstrates = hasSequenceMatch ? firstSequenceMatch["substrates"] : null;
    const [overrideSubstrates, setOverrideSubstrates] = useState(false);
    const [annotationType, setAnnotationType] = useState(null);
    const isAnnotated = useMemo(() => Boolean(annotationType), [annotationType]);


    const [proteinName, setProteinName] = useState(protein_name || '');
    const [isDuplicateDomain, setIsDuplicateDomain] = useState(false);

    useEffect(() => {
        setProteinName(protein_name);
    }, [protein_name]);

    const domainName = useMemo(() => {
        return matchSynonym != null
            ? matchSynonym
            : `${proteinName}.A${domainIndex}`;
    }, [matchSynonym, proteinName, domainIndex]);

    const domainSynonym = useMemo(() => {
        return `${proteinName}.A${domainIndex}`;
    }, [proteinName, domainIndex]);

    // the annotation type depends on whether the domain is already in the
    // dataset, so recompute it when that answer comes back; only then, since
    // substrate edits recompute it themselves
    useEffect(() => {
        const hasValidSubstrate = substrates?.some(
            sub => sub.substrateName && sub.substrateSmiles
        );

        if (hasValidSubstrate) {
            recalculateAnnotationType(substrates);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDuplicateDomain]);

    useEffect(() => {
        const fetchDuplicateStatus = async () => {
            if (domainName) {
                try {
                    const response = await fetch('/api/check_domain_name', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({domain_name: domainName}),
                    });

                    const data = await response.json();
                    setIsDuplicateDomain(data.domain_in_dataset ?? false);
                } catch (error) {
                    console.error('Error checking domain name:', error);
                    setIsDuplicateDomain(false);
                }
            }
        };

        fetchDuplicateStatus();
    }, [domainName]);

    // Initialize with one substrate entry, default to no selection & no custom smiles
    const [substrates, setSubstrates] = useState([
        {
            selectedSubstrate: null,
            useCustomSmiles: false,
            customSmiles: '',
            newSubstrateName: '',
            substrateName: '',
            substrateSmiles: '',
            sequence: sequence,
            signature: signature,
            extendedSignature: extendedSignature
        },
    ]);

    const [smilesOptions, setSmilesOptions] = useState([]);

    // Helper to compare known vs selected (dropdown-based) substrates
    const matchedNames = new Set((matchedSubstrates || []).map(s => s.name));

    const selectedDropdownNames = new Set(
        substrates
            .filter(sub => !sub.useCustomSmiles && sub.selectedSubstrate)
            .map(sub => sub.selectedSubstrate.name)
    );

    const allSelectedAreMatched = selectedDropdownNames.size === matchedNames.size &&
        [...selectedDropdownNames].every(name => matchedNames.has(name));

    const showNoUpdateMessage = hasSequenceMatch && allSelectedAreMatched;

    const recalculateAnnotationType = (substrates) => {
        const matchedNames = new Set((matchedSubstrates || []).map(s => s.name));

        const selectedDropdownNames = new Set(
            substrates
                .filter(sub => !sub.useCustomSmiles && sub.selectedSubstrate)
                .map(sub => sub.selectedSubstrate.name)
        );

        const hasDropdownSelections = selectedDropdownNames.size > 0;
        const hasCustomSmiles = substrates.some(sub => sub.useCustomSmiles);

        const allMatch =
            selectedDropdownNames.size === matchedNames.size &&
            [...selectedDropdownNames].every(name => matchedNames.has(name));

        let type = null;

        // --- Branch 1: Duplicate domain with no sequence match
        if (!hasSequenceMatch && isDuplicateDomain) {
            type = 'duplicate_entry';

            // --- Branch 2: All dropdown-based (no custom SMILES)
        } else if (hasDropdownSelections && !hasCustomSmiles) {
            if (hasSequenceMatch && allMatch) {
                type = 'no_update';
            } else if (hasSequenceMatch) {
                type = 'correction';
            } else {
                type = 'new_entry';
            }

            // --- Branch 3: All custom SMILES (no dropdown)
        } else if (!hasDropdownSelections && hasCustomSmiles) {
            type = hasSequenceMatch ? 'correction' : 'new_entry';

            // --- Branch 4: Mixed dropdown + custom SMILES
        } else if (hasDropdownSelections && hasCustomSmiles) {
            // Decide your intended behavior for mixed mode
            type = hasSequenceMatch ? 'correction' : 'new_entry';
        }

        onAnnotationChange?.(domainName, substrates.map(s => ({ ...s })), type);

        setAnnotationType(type);
    };

    // Load SMILES for substrate assignment
    useEffect(() => {
        fetch('/api/get_substrates')
            .then(res => res.json())
            .then(data => {
                setSmilesOptions(data);
            })
            .catch(err => {
                console.error('Failed to load smiles data from API:', err);
            });
    }, []);

    // Sort smilesOptions by PARAS predictions order
    const [sortedOptions, setSortedOptions] = useState([]);

    const predictions = parasResult["predictions"];
    useEffect(() => {
        if (!smilesOptions.length || !predictions) return;

        const preferredOrder = predictions.map(p => p["substrate_name"]);
        const orderMap = new Map(preferredOrder.map((name, i) => [name, i]));

        const sorted = [...smilesOptions].sort((a, b) => {
            const aIndex = orderMap.has(a["name"]) ? orderMap.get(a["name"]) : Infinity;
            const bIndex = orderMap.has(b["name"]) ? orderMap.get(b["name"]) : Infinity;
            if (aIndex !== bIndex) return aIndex - bIndex;
            return a["name"].localeCompare(b["name"]);
        });

        setSortedOptions(sorted);
    }, [smilesOptions, predictions]);

    // The first prediction for showing
    const selectedPrediction = parasResult['predictions'][0];


    // Handlers to update substrate entries
    const updateSubstrateField = (index, field, value) => {
        setSubstrates((prev) => {
            const newSubs = [...prev];
            const updated = {...newSubs[index], [field]: value};

            if (field === 'useCustomSmiles') {
                if (value === true) {
                    updated.selectedSubstrate = null;
                    updated.substrateName = '';
                    updated.substrateSmiles = '';
                } else {

                    updated.customSmiles = '';
                    updated.newSubstrateName = '';
                    updated.substrateName = '';
                    updated.substrateSmiles = '';
                }
            }

            if (field === 'selectedSubstrate' && value !== null) {
                updated.useCustomSmiles = false;
                updated.customSmiles = '';
                updated.newSubstrateName = '';
                updated.substrateName = value.name;
                updated.substrateSmiles = value.smiles;
            }

            // clearing the selection must not leave the previous substrate behind
            if (field === 'selectedSubstrate' && value === null) {
                updated.substrateName = '';
                updated.substrateSmiles = '';
            }

            newSubs[index] = updated;

            recalculateAnnotationType(newSubs);
            return newSubs;
        });
    };
    // Keyed on the typed names/SMILES only: the effect writes substrateName and
    // substrateSmiles back into `substrates`, so depending on the whole array
    // would re-run it (and re-fetch) on its own update.
    const customSubstrateKey = substrates.map((s) => `${s.newSubstrateName}|${s.customSmiles}`).join('|');
    const newSubstrateNamesKey = substrates.map((s) => s.newSubstrateName).join('|');

    useEffect(() => {
        substrates.forEach((sub, i) => {
            const name = sub.newSubstrateName?.trim();
            const smiles = sub.customSmiles?.trim();

            if (sub.useCustomSmiles && name) {
                fetch('/api/check_substrate_name', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({substrate_name: name}),
                })
                    .then((res) => res.json())
                    .then((data) => {
                        const duplicate = Array.isArray(data) && data.length > 0;

                        setNameWarnings((prev) => ({
                            ...prev,
                            [i]: duplicate
                                ? 'Substrate name already exists in the dataset.'
                                : null,
                        }));

                        setSubstrates((prev) => {
                            const updated = [...prev];
                            const item = {...updated[i]};

                            if (!duplicate && name && smiles) {
                                item.substrateName = name;
                                item.substrateSmiles = smiles;
                            } else {
                                item.substrateName = '';
                                item.substrateSmiles = '';
                            }

                            updated[i] = item;
                            recalculateAnnotationType(updated)
                            return updated;
                        });
                    })
                    .catch((err) => {
                        console.error('Failed to check substrate name:', err);
                        setNameWarnings((prev) => ({
                            ...prev,
                            [i]: 'Error checking substrate name.',
                        }));
                    });
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customSubstrateKey]);

    useEffect(() => {
        substrates.forEach((sub, i) => {
            const name = sub.newSubstrateName?.trim();
            if (sub.useCustomSmiles && name) {
                fetch('/api/check_substrate_name', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({substrate_name: name}),
                })
                    .then((res) => res.json())
                    .then((data) => {
                        const isDuplicate = Array.isArray(data) && data.length > 0;
                        setNameWarnings((prev) => ({
                            ...prev,
                            [i]: isDuplicate ? `Substrate name already exists in the dataset.` : null,
                        }));
                    })
                    .catch((err) => {
                        console.error('Failed to check substrate name:', err);
                        setNameWarnings((prev) => ({
                            ...prev,
                            [i]: 'Error checking substrate name.',
                        }));
                    });
            } else {
                setNameWarnings((prev) => ({
                    ...prev,
                    [i]: null,
                }));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newSubstrateNamesKey]);

    return (
        <Box
            sx={{
                flexGrow: 1,
                borderRadius: '11px',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 2,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                // a domain already in the database is shown sunken rather than
                // greyed, so it still reads as a card in either colour mode
                backgroundColor: hasSequenceMatch ? 'surface.sunken' : 'background.paper',
            }}
        >
            {/* Header with collapse toggle */}
            <Box
                sx={{
                    backgroundColor: hasSequenceMatch ? 'surface.borderStrong' : 'accent.main',
                    color: hasSequenceMatch ? 'text.primary' : 'accent.contrastText',
                    fontWeight: 600,
                    padding: '14px 8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                {/* Left side: domain info + optional warning stacked vertically */}
                <Box sx={{display: 'flex', flexDirection: 'column'}}>
                    <Typography
                        sx={{
                            px: 1,
                        }}
                    >
                        {`Domain ${parasResult['domain_nr']}: ${domainSynonym} (${parasResult['domain_start']}-${parasResult['domain_end']})`}
                    </Typography>

                    {hasSequenceMatch && (
                        <Typography
                            variant="body2"
                            sx={{marginTop: 0.5, fontSize: '0.9em', color: 'black'}}
                        >
                            Sequence already found in dataset ({matchSynonym})
                        </Typography>
                    )}
                </Box>

                {/* Right side: expand/collapse button */}
                {/* <IconButton onClick={toggleExpanded} size="small">
                    {expanded ? <ExpandLess/> : <ExpandMore/>}
                </IconButton> */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={excluded}
                        onChange={(e) => onExcludedChange?.(e.target.checked)}
                        sx={{ color: 'inherit', '&.Mui-checked': { color: 'inherit' } }}
                      />
                    }
                    label="Exclude"
                    sx={{ m: 0, whiteSpace: 'nowrap' }}
                  />
                  {excluded ? (
                    <Tooltip title="Excluded from submission" arrow>
                      <Block fontSize="small" aria-label="Excluded from submission" />
                    </Tooltip>
                  ) : isAnnotated && (
                    <Tooltip title="Annotated" arrow>
                      <CheckCircle
                        fontSize="small"
                        sx={{ color: 'success.main' }}
                        aria-label="Annotated"
                      />
                    </Tooltip>
                  )}
                  <IconButton onClick={toggleExpanded} size="small" aria-label={expanded ? 'Collapse' : 'Expand'}>
                    {expanded ? <ExpandLess/> : <ExpandMore/>}
                  </IconButton>
                </Box>
            </Box>

            {/* Collapsible content; kept visible but inert while excluded */}
            <Collapse in={expanded}>
                {excluded && (
                    <Typography sx={{px: 2, pt: 2, color: 'text.secondary', fontStyle: 'italic'}}>
                        This domain is excluded from the submission.
                    </Typography>
                )}
                <Box
                    sx={{
                        padding: 2,
                        opacity: excluded ? 0.5 : 1,
                        pointerEvents: excluded ? 'none' : 'auto',
                    }}
                    aria-disabled={excluded}
                >

                    {/* domain signature */}
                    {parasResult['domain_signature'].length > 0 && (
                        <Box
                            sx={{
                                padding: 1,
                                display: 'flex',
                                justifyContent: 'left',
                            }}
                        >
                            <FaFingerprint size="1.5em" style={{marginRight: '5px'}}/>
                            {parasResult['domain_signature']}
                        </Box>
                    )}

                    {/* spacing */}
                    <Box sx={{height: 16}}/>

                    {/* prediction + substrate selectors + visualizer */}
                    <Box sx={{display: 'flex', alignItems: 'flex-start', paddingX: 1, gap: 2, flexDirection: 'column'}}>
                        {/* Show known substrates if there's a match */}
                        {hasSequenceMatch && (
                            <>
                                <Typography sx={{fontWeight: 500}}>Known substrates</Typography>
                                <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                                    {matchedSubstrates.map((substrate, idx) => (
                                        <Box key={idx}
                                             sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                            <SmileDrawerContainer
                                                identifier={`${parasResult['domain_name']}-${parasResult['domain_nr']}-match-${idx}`}
                                                smilesStr={substrate.smiles}
                                                height={200}
                                                width={200}
                                            />
                                            <Typography variant="caption" sx={{mt: 1}}>{substrate.name}</Typography>
                                        </Box>
                                    ))}
                                </Box>

                                {/* Override checkbox */}
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={overrideSubstrates}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setOverrideSubstrates(checked);
                                                if (!checked) {
                                                    setSubstrates([]);
                                                    setAnnotationType(null);
                                                    onAnnotationChange?.(domainName, [], null);
                                                }
                                            }}
                                        />
                                    }
                                    label="Make substrate correction"
                                    sx={{mt: 2}}
                                />
                            </>
                        )}

                        {/* Render override UI if checked or no match */}
                        {(!hasSequenceMatch || overrideSubstrates) && (
                            <>


                                {/* Render all substrate selector blocks with visualizer */}
                                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                    {/* Show PARAS prediction only if no sequence match */}
                                    {!hasSequenceMatch && selectedPrediction && (
                                        <Box>
                                            <Box
                                                sx={{
                                                    fontWeight: 500,
                                                    marginBottom: 0.5
                                                }}
                                            >

                                            </Box>

                                            <Box sx={{fontWeight: 500, marginBottom: 0.5}}>
                                                PARAS prediction
                                            </Box>
                                            <Box
                                                sx={{
                                                    border: 1, borderColor: 'divider',
                                                    borderRadius: '4px',
                                                    padding: '8px',
                                                    minHeight: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-start',
                                                    textAlign: 'left',
                                                }}
                                            >
                                                {`${selectedPrediction['substrate_name']} (${selectedPrediction['probability'].toFixed(2)})`}
                                            </Box>
                                        </Box>
                                    )}
                                    <Typography sx={{fontWeight: 500}}>Substrate assignments</Typography>
                                    {substrates.map((sub, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                alignItems: 'flex-start',
                                                border: 1, borderColor: 'divider',
                                                borderRadius: 1,
                                                p: 1,
                                                minWidth: 500,
                                                position: 'relative',
                                            }}
                                        >
                                            {/* Substrate selector box */}
                                            <Box sx={{flex: '1 1 300px'}}>
                                                <Box sx={{
                                                    fontWeight: 500,
                                                    marginBottom: 0.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between'
                                                }}>
                                <span>
                                    {sub.substrateName?.trim()
                                        ? `Substrate ${i + 1}: ${sub.substrateName}`
                                        : `Substrate ${i + 1}`}
                                </span>
                                                    {i > 0 && (
                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            size="small"
                                                            onClick={() =>
                                                                setSubstrates((prev) => {
                                                                    const updated = prev.filter((_, idx) => idx !== i);
                                                                    recalculateAnnotationType(updated);
                                                                    return updated;
                                                                })
                                                            }
                                                            sx={{ml: 1, minWidth: 'auto', padding: '2px 8px'}}
                                                        >
                                                            Remove
                                                        </Button>
                                                    )}
                                                </Box>

                                                <Autocomplete
                                                    options={sortedOptions}
                                                    getOptionLabel={(option) => option['name']}
                                                    renderInput={(params) => <TextField {...params}
                                                                                        variant="outlined"/>}
                                                    value={sub.selectedSubstrate || null}
                                                    onChange={(event, newValue) => updateSubstrateField(i, 'selectedSubstrate', newValue)}
                                                    isOptionEqualToValue={(option, value) => option['name'] === value['name']}
                                                    clearOnEscape
                                                    disabled={sub.useCustomSmiles}
                                                    getOptionDisabled={(option) => {
                                                        // Disable if this substrate name is already selected in other entries (not counting current index)
                                                        return substrates.some((s, idx) =>
                                                            idx !== i &&
                                                            !s.useCustomSmiles &&
                                                            s.selectedSubstrate?.name === option.name
                                                        );
                                                    }}
                                                    PopperComponent={(props) => (
                                                        <Popper
                                                            {...props}
                                                            placement="bottom-start"
                                                            modifiers={[{name: 'flip', enabled: false}]}
                                                        />
                                                    )}
                                                />

                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={sub.useCustomSmiles}
                                                            onChange={(e) => updateSubstrateField(i, 'useCustomSmiles', e.target.checked)}
                                                        />
                                                    }
                                                    label="New substrate"
                                                />

                                                {sub.useCustomSmiles && (
                                                    <>
                                                        <Box sx={{mt: 1}}>
                                                            <TextField
                                                                label="Enter substrate name"
                                                                fullWidth
                                                                value={sub.newSubstrateName}
                                                                onChange={(e) => updateSubstrateField(i, 'newSubstrateName', e.target.value)}
                                                                error={Boolean(nameWarnings[i])}
                                                                helperText={nameWarnings[i] || ''}
                                                            />
                                                        </Box>
                                                        <Box sx={{mt: 1}}>
                                                            <SmilesChecker
                                                                key={i}
                                                                i={i}
                                                                customSmiles={sub.customSmiles}
                                                                updateSubstrateField={updateSubstrateField}
                                                            />
                                                        </Box>
                                                    </>
                                                )}
                                            </Box>

                                            {/* Visualise the substrate */}
                                            <Box sx={{width: 200, height: 200, flexShrink: 0}}>
                                                {/* Prioritize custom SMILES, then selected substrate */}
                                                {sub.useCustomSmiles && sub.customSmiles ? (
                                                    <SmileDrawerContainer
                                                        identifier={`${parasResult['domain_name']}-${parasResult['domain_nr']}-custom-${i}`}
                                                        smilesStr={sub.customSmiles}
                                                        height={200}
                                                        width={200}
                                                    />

                                                ) : sub.selectedSubstrate ? (
                                                    <SmileDrawerContainer
                                                        identifier={`${parasResult['domain_name']}-${parasResult['domain_nr']}-sel-${i}`}
                                                        smilesStr={sub.selectedSubstrate['smiles']}
                                                        height={200}
                                                        width={200}
                                                    />
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            height: 200,
                                                            width: 200,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: 'text.secondary',
                                                            fontStyle: 'italic',
                                                            border: '1px dashed', borderColor: 'divider',
                                                        }}
                                                    >
                                                        No substrate selected
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    ))}

                                    {/* If the chosen substrates exactly match the given substrates, show message */}

                                    {showNoUpdateMessage && (
                                        <Box
                                            sx={{
                                                border: 1,
                                                borderColor: 'success.main',
                                                backgroundColor: 'surface.sunken',
                                                borderRadius: 1,
                                                padding: 2,
                                                mb: 2,
                                            }}
                                        >
                                            <Typography sx={{fontWeight: 500, color: 'success.main'}}>
                                                Selected substrates match known substrates exactly. No update will be
                                                made.
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Add substrate button */}
                                    <Button
                                        variant="outlined"
                                        onClick={() =>
                                            setSubstrates((prev) => [
                                                ...prev,
                                                {
                                                    selectedSubstrate: null,
                                                    useCustomSmiles: false,
                                                    customSmiles: '',
                                                    newSubstrateName: '',
                                                    sequence: sequence,
                                                    signature: signature,
                                                    extendedSignature: extendedSignature
                                                },
                                            ])
                                        }
                                        sx={{alignSelf: 'flex-start', mb: 2}}
                                    >
                                        Add another substrate
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>

                </Box>
            </Collapse>
        </Box>
    );
};

export default DomainTile;
